import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { Navigate } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { CourseCompletionRuleOptions } from 'app/library/course-offering/models/course-completion-rule-options.model';
import { ProgramCompletionRuleOptions } from 'app/library/course-offering/models/program-completion-rule-options.model';
import { ModuleFeatureService } from 'app/library/module-feature/services/module-feature.service';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { ModuleType } from 'app/library/module/enums/moduleType.enum';
import { SectionRule } from 'app/library/module/enums/sectionRule.enum';
import { IAutomaticallyBySetRules } from 'app/library/module/interfaces/automatically-by-set-rules.interface';
import { Module } from 'app/library/module/models/module.model';
import { ModuleService } from 'app/library/module/services/module.service';
import { Section } from 'app/library/section/models/section.model';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { ModuleFeatureSwitchName } from 'app/projects/shared/src/lib/enums/module-feature-switch.enum';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { CompletionRule } from '../../../../course-offering/enums/completion-rule.enum';
import { IChangeProgramCompletionDialogResult } from './change-program-completion-dialog-result.interface';

@Component({
    selector: 'app-change-program-completion-dialog',
    templateUrl: './change-program-completion-dialog.component.html',
    styleUrls: ['./change-program-completion-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ChangeProgramCompletionDialogComponent implements OnInit, AfterViewInit {
    @Input()
    programCompletionRule: CompletionRule;

    @Input()
    moduleSections: Section[];

    @Input()
    module: Module;

    @Input()
    title: string;

    @Input()
    text: string;

    private _programCompletionForm: FormGroup;

    get programCompletionForm(): FormGroup {
        return this._programCompletionForm;
    }

    get completionRuleCategoryControl() {
        return this.programCompletionForm.get('completion_rule_category');
    }

    get completionRuleControl() {
        return this.programCompletionForm.get('completion_rule');
    }

    get selectedSectionControl() {
        return this.programCompletionForm.get('selected_section');
    }

    get sectionsCompletionPercentageControl() {
        return this.programCompletionForm.get('sections_completion_percentage');
    }

    get minimumAttendanceControl() {
        return this.programCompletionForm.get('minimum_attendance');
    }

    completionRuleOpts = CompletionRule;
    SectionRule = SectionRule;
    automaticallyBySetRulesOptions: IAutomaticallyBySetRules[] = [];

    isProgram = false;
    isCourse = false;
    isAutomaticCompletionSelected = false;
    showSectionsFields = false;
    showPercentageField = false;
    showMinimumAttendanceField = false;
    showCourseMinimumAttendanceField = false;
    showAllCoursesMinAttendanceCheckbox = false;
    showEndDateMinAttendanceCheckbox = false;
    isMinimumAttendanceAvailable = false;
    isSaveDisabled = true;

    allCoursesAndMinAttendanceReach = new FormControl(false);
    autoEndDateReachAndMinAttendanceReached = new FormControl(false);

    manuallyLabelKey = 'COURSES.COMPLETION_RULE.MANUALLY';
    automaticallyLabelKey = 'COURSES.COMPLETION_RULE.AUTOMATICALLY';

    constructor(
        private _formBuilder: FormBuilder,
        private _featureSwitchPipe: FeatureSwitchPipe,
        private _moduleFeatureService: ModuleFeatureService,
        private _moduleService: ModuleService,
        private _store: Store,
        public dialogRef: MatDialogRef<ChangeProgramCompletionDialogComponent>,
        private _cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.isProgram =
            this.module?.kind === ModuleKind.ProgramTemplate ||
            this.module?.kind === ModuleKind.ProgramExecution ||
            this.module?.type === ModuleType.Program;
        this.isCourse = !this.isProgram;
        this.manuallyLabelKey = this.isProgram ? 'PROGRAMS.COMPLETION_RULE.MANUALLY_FULL' : 'COURSES.COMPLETION_RULE.MANUALLY';
        this.automaticallyLabelKey = this.isProgram ? 'PROGRAMS.COMPLETION_RULE.AUTOMATICALLY_FULL' : 'COURSES.COMPLETION_RULE.AUTOMATICALLY';
        this.isMinimumAttendanceAvailable = this._isMinimumAttendanceAvailable();

        this._initOptions();
        this._initForm();
        this._hydrateMinAttendanceCheckboxes();
        this._syncConditionalFields();
        this._updateSaveDisabled();
    }

    async ngAfterViewInit(): Promise<void> {
        await this._hydrateFromCurrentRule();
        this._syncConditionalFields();
        this._updateSaveDisabled();
        this._cd.detectChanges();
    }

    handleCompletionRuleCategoryChange($event: MatRadioChange): void {
        if ($event.value === CompletionRule.MANUALLY) {
            this.completionRuleControl.disable();
            this.completionRuleControl.setValue(null);
            this.sectionsCompletionPercentageControl.clearValidators();
            this.sectionsCompletionPercentageControl.setValue(null);
            this.minimumAttendanceControl.clearValidators();
            this.minimumAttendanceControl.setValue(null);
            this._clearMinAttendanceCheckboxes();
        } else {
            this.completionRuleControl.enable();
            if (!this.completionRuleControl.value) {
                const defaultRule = this.isCourse ? CompletionRule.SECTIONS : CompletionRule.AUTO_COMPLETE_ALL_COURSES;
                this.completionRuleControl.setValue(defaultRule);
                if (defaultRule === CompletionRule.SECTIONS) {
                    this.selectedSectionControl.setValue('all');
                }
            }
        }

        this._syncConditionalFields();
        this._updateSaveDisabled();
    }

    handleCompletionRuleChange(): void {
        this._syncConditionalFields();
        this._updateSaveDisabled();
    }

    handleSelectedSectionChange(): void {
        this._syncConditionalFields();
        this._updateSaveDisabled();
    }

    onConditionalFieldChange(): void {
        this._updateSaveDisabled();
    }

    clampPercentageValue(controlName: 'minimum_attendance' | 'sections_completion_percentage'): void {
        const control = this.programCompletionForm.get(controlName);
        if (!control) {
            return;
        }

        const rawValue = control.value;
        if (rawValue === null || rawValue === undefined || rawValue === '') {
            this._updateSaveDisabled();
            return;
        }

        const value = Number(rawValue);
        if (Number.isNaN(value)) {
            this._updateSaveDisabled();
            return;
        }

        if (value > 100) {
            control.setValue(100, { emitEvent: false });
        } else if (value < 1) {
            control.setValue(1, { emitEvent: false });
        }

        this._updateSaveDisabled();
    }

    onMinAttendanceCheckboxChange(): void {
        this._syncConditionalFields();
        this._updateSaveDisabled();
    }

    openSettings(): void {
        this.dialogRef.close(false);
        this._store.dispatch(new Navigate([`/modules/${this.module.id}/edit/settings`]));
    }

    onSaveProgramCompletion(): void {
        if (this.isSaveDisabled) {
            return;
        }

        const result = this._buildResult();
        this.dialogRef.close(result);
    }

    private _initOptions(): void {
        if (this.isProgram) {
            this.automaticallyBySetRulesOptions = ProgramCompletionRuleOptions.all
                .filter((option) => !this.module?.selfPaced || option.value !== CompletionRule.AUTO_END_DATE_REACHED)
                .map((option) => ({ ...option }));
            return;
        }

        const isELearning = this.module?.type === ModuleType.ELearning || !!this.module?.selfPaced;
        const source = isELearning ? CourseCompletionRuleOptions.forELearning : CourseCompletionRuleOptions.all;

        this.automaticallyBySetRulesOptions = source
            .filter((option) => this.isMinimumAttendanceAvailable || option.value !== CompletionRule.AUTO_MIN_ATTENDANCE_REACHED)
            .filter((option) => !this.module?.selfPaced || option.value !== CompletionRule.AUTO_END_DATE_REACHED)
            .map((option) => ({ ...option }));
    }

    private _isMinimumAttendanceAvailable(): boolean {
        if (this.module?.selfPaced) {
            return false;
        }

        const isTenantAttendanceEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.Attendance);

        // Programs: BE enables the module attendance feature when rules 6/7 are chosen (same options as create).
        if (this.isProgram) {
            return isTenantAttendanceEnabled;
        }

        const isModuleAttendanceActive = this._moduleFeatureService.checkModuleFeatureStatus(ModuleFeatureSwitchName.Attendance);

        return isTenantAttendanceEnabled && isModuleAttendanceActive;
    }

    private _initForm(): void {
        const isManual = this.programCompletionRule === CompletionRule.MANUALLY;

        this._programCompletionForm = this._formBuilder.group({
            completion_rule_category: [isManual ? CompletionRule.MANUALLY : CompletionRule.UNDEFINED],
            completion_rule: [{ value: isManual ? null : this._mapRuleToUiValue(this.programCompletionRule), disabled: isManual }],
            selected_section: [this._mapSelectedSectionValue(this.programCompletionRule)],
            sections_completion_percentage: [this.module?.sectionsCompletionPercentage ?? null],
            minimum_attendance: [null],
        });

        this.programCompletionForm.valueChanges.subscribe(() => this._updateSaveDisabled());
    }

    private _hydrateMinAttendanceCheckboxes(): void {
        if (!this.isProgram || !this.isMinimumAttendanceAvailable) {
            return;
        }

        this.allCoursesAndMinAttendanceReach.setValue(
            this.programCompletionRule === CompletionRule.AUTO_COMPLETE_ALL_COURSES_AND_MIN_ATTENDANCE_REACHED,
            { emitEvent: false }
        );
        this.autoEndDateReachAndMinAttendanceReached.setValue(
            this.programCompletionRule === CompletionRule.AUTO_END_DATE_REACHED_AND_MIN_ATTENDANCE_REACHED,
            { emitEvent: false }
        );
    }

    private async _hydrateFromCurrentRule(): Promise<void> {
        const needsAttendance =
            this.programCompletionRule === CompletionRule.AUTO_MIN_ATTENDANCE_REACHED ||
            this.programCompletionRule === CompletionRule.AUTO_COMPLETE_ALL_COURSES_AND_MIN_ATTENDANCE_REACHED ||
            this.programCompletionRule === CompletionRule.AUTO_END_DATE_REACHED_AND_MIN_ATTENDANCE_REACHED;

        if (!needsAttendance || !this.isMinimumAttendanceAvailable) {
            return;
        }

        try {
            const attendance = await this._moduleService.getMinimumAttendance(this.module.id);
            this.minimumAttendanceControl.setValue(attendance?.minimumAttendance ?? null, { emitEvent: false });
        } catch {
            this.minimumAttendanceControl.setValue(null, { emitEvent: false });
        }
    }

    private _mapRuleToUiValue(rule: CompletionRule): CompletionRule | null {
        if (rule == null || rule === CompletionRule.MANUALLY || rule === CompletionRule.UNDEFINED) {
            return null;
        }

        if (
            rule === CompletionRule.AUTO_ALL_SECTIONS ||
            rule === CompletionRule.AUTO_MANDATORY_SECTIONS ||
            rule === CompletionRule.AUTO_SPECIFIED_SECTIONS ||
            rule === CompletionRule.AUTO_PERCENTAGE_OF_SECTIONS
        ) {
            return CompletionRule.SECTIONS;
        }

        if (rule === CompletionRule.AUTO_COMPLETE_ALL_COURSES_AND_MIN_ATTENDANCE_REACHED) {
            return CompletionRule.AUTO_COMPLETE_ALL_COURSES;
        }

        if (rule === CompletionRule.AUTO_END_DATE_REACHED_AND_MIN_ATTENDANCE_REACHED) {
            return CompletionRule.AUTO_END_DATE_REACHED;
        }

        return rule;
    }

    private _mapSelectedSectionValue(rule: CompletionRule): string {
        if (rule === CompletionRule.AUTO_PERCENTAGE_OF_SECTIONS) {
            return 'percentage';
        }

        if (rule === CompletionRule.AUTO_MANDATORY_SECTIONS) {
            return 'mandatory';
        }

        if (rule === CompletionRule.AUTO_SPECIFIED_SECTIONS) {
            const section = (this.moduleSections || []).find((item) => item.isConditionForModuleCompletion);
            return section?.id || 'all';
        }

        return 'all';
    }

    private _syncConditionalFields(): void {
        const isAutomatic = this.completionRuleCategoryControl.value !== CompletionRule.MANUALLY;
        const selectedRule = this.completionRuleControl.value;
        const selectedSection = this.selectedSectionControl.value;

        this.isAutomaticCompletionSelected = isAutomatic;
        this.showSectionsFields = this.isCourse && isAutomatic && selectedRule === CompletionRule.SECTIONS;
        this.showPercentageField = this.showSectionsFields && selectedSection === 'percentage';
        this.showCourseMinimumAttendanceField =
            this.isCourse && this.isMinimumAttendanceAvailable && isAutomatic && selectedRule === CompletionRule.AUTO_MIN_ATTENDANCE_REACHED;

        this._syncMinAttendanceCheckboxes(isAutomatic, selectedRule);

        this.showMinimumAttendanceField =
            this.showCourseMinimumAttendanceField ||
            (this.showAllCoursesMinAttendanceCheckbox && !!this.allCoursesAndMinAttendanceReach.value) ||
            (this.showEndDateMinAttendanceCheckbox && !!this.autoEndDateReachAndMinAttendanceReached.value);

        if (this.showPercentageField) {
            this.sectionsCompletionPercentageControl.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
        } else {
            this.sectionsCompletionPercentageControl.clearValidators();
            if (selectedSection !== 'percentage') {
                this.sectionsCompletionPercentageControl.setValue(null, { emitEvent: false });
            }
        }
        this.sectionsCompletionPercentageControl.updateValueAndValidity({ emitEvent: false });

        if (this.showMinimumAttendanceField) {
            this.minimumAttendanceControl.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
        } else {
            this.minimumAttendanceControl.clearValidators();
        }
        this.minimumAttendanceControl.updateValueAndValidity({ emitEvent: false });
    }

    private _syncMinAttendanceCheckboxes(isAutomatic: boolean, selectedRule: CompletionRule): void {
        const canShowAttendance = this.isProgram && this.isMinimumAttendanceAvailable && isAutomatic;

        this.showAllCoursesMinAttendanceCheckbox = canShowAttendance && selectedRule === CompletionRule.AUTO_COMPLETE_ALL_COURSES;
        this.showEndDateMinAttendanceCheckbox = canShowAttendance && selectedRule === CompletionRule.AUTO_END_DATE_REACHED;

        if (!this.showAllCoursesMinAttendanceCheckbox) {
            this.allCoursesAndMinAttendanceReach.setValue(false, { emitEvent: false });
        }

        if (!this.showEndDateMinAttendanceCheckbox) {
            this.autoEndDateReachAndMinAttendanceReached.setValue(false, { emitEvent: false });
        }
    }

    private _clearMinAttendanceCheckboxes(): void {
        this.allCoursesAndMinAttendanceReach.setValue(false, { emitEvent: false });
        this.autoEndDateReachAndMinAttendanceReached.setValue(false, { emitEvent: false });
        this.showAllCoursesMinAttendanceCheckbox = false;
        this.showEndDateMinAttendanceCheckbox = false;
    }

    private _updateSaveDisabled(): void {
        if (this.completionRuleCategoryControl.value === CompletionRule.MANUALLY) {
            this.isSaveDisabled = false;
            return;
        }

        if (!this.completionRuleControl.value) {
            this.isSaveDisabled = true;
            return;
        }

        if (this.showPercentageField && this.sectionsCompletionPercentageControl.invalid) {
            this.isSaveDisabled = true;
            return;
        }

        if (this.showMinimumAttendanceField && this.minimumAttendanceControl.invalid) {
            this.isSaveDisabled = true;
            return;
        }

        this.isSaveDisabled = false;
    }

    private _buildResult(): IChangeProgramCompletionDialogResult {
        if (this.completionRuleCategoryControl.value === CompletionRule.MANUALLY) {
            return {
                selectedCompletionRule: CompletionRule.MANUALLY,
                requiredSectionForCompletion: null,
                sectionsCompletionPercentage: null,
                minimumAttendance: null,
            };
        }

        let selectedCompletionRule = this.completionRuleControl.value as CompletionRule;
        let requiredSectionForCompletion: string | null = null;
        let sectionsCompletionPercentage: number | null = null;
        let minimumAttendance: number | null = null;

        if (selectedCompletionRule === CompletionRule.SECTIONS) {
            const selectedSection = this.selectedSectionControl.value;
            if (selectedSection === 'percentage') {
                selectedCompletionRule = CompletionRule.AUTO_PERCENTAGE_OF_SECTIONS;
                sectionsCompletionPercentage = Number(this.sectionsCompletionPercentageControl.value);
            } else if (selectedSection === 'mandatory') {
                selectedCompletionRule = CompletionRule.AUTO_MANDATORY_SECTIONS;
            } else if (selectedSection === 'all') {
                selectedCompletionRule = CompletionRule.AUTO_ALL_SECTIONS;
            } else {
                selectedCompletionRule = CompletionRule.AUTO_SPECIFIED_SECTIONS;
                requiredSectionForCompletion = selectedSection;
            }
        }

        if (selectedCompletionRule === CompletionRule.AUTO_MIN_ATTENDANCE_REACHED) {
            minimumAttendance = Number(this.minimumAttendanceControl.value);
        }

        if (this.isProgram && this.isMinimumAttendanceAvailable) {
            if (selectedCompletionRule === CompletionRule.AUTO_COMPLETE_ALL_COURSES && this.allCoursesAndMinAttendanceReach.value) {
                selectedCompletionRule = CompletionRule.AUTO_COMPLETE_ALL_COURSES_AND_MIN_ATTENDANCE_REACHED;
                minimumAttendance = Number(this.minimumAttendanceControl.value);
            } else if (selectedCompletionRule === CompletionRule.AUTO_END_DATE_REACHED && this.autoEndDateReachAndMinAttendanceReached.value) {
                selectedCompletionRule = CompletionRule.AUTO_END_DATE_REACHED_AND_MIN_ATTENDANCE_REACHED;
                minimumAttendance = Number(this.minimumAttendanceControl.value);
            }
        }

        return {
            selectedCompletionRule,
            requiredSectionForCompletion,
            sectionsCompletionPercentage,
            minimumAttendance,
        };
    }
}
